import { withNamespaces } from "react-i18next";
import React, { memo, useState, useEffect, useMemo, useCallback } from "react";
import { Alert, Button, Col, Container, Row } from "reactstrap";
import { useLocation, useParams, useHistory } from "react-router-dom";
import { withRouter } from "react-router-dom";
import Tabs from "./tabs";
import Navigator from "../../../../components/navigator";
import DataProtectionSoftwareInformation from "./tabs-content/information/index";
import InternalCommunication from "./tabs-content/internal-communication";
import ProcessingActivity from "./tabs-content/processing-activity";
import DPA from "./tabs-content/dpa";
import DPIA from "./tabs-content/dpia/index";
import RiskAssessment from "./tabs-content/risk-assessment";
import TaskManager from "./tabs-content/task-manager";
import RiskDetails from "src/modules/data-protection/components/RisksAndTasks/risks/details";
import TaskDetails from "src/modules/data-protection/components/RisksAndTasks/tasks/details";
import RelatedIncident from "./tabs-content/related-incident";
import HistoryLog from "./tabs-content/history-log";
import Findings from "./tabs-content/monitoring";
import FindingDetails from "./tabs-content/monitoring/components/details";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AIRiskAssessment from "./tabs-content/ai-risk-assessment"
import Compliance from "./tabs-content/compliance";
import Spinner from "react-bootstrap/Spinner";

const DetailPage = ({
    detailPage,
    setDetailPage,
    softwareData
}) => {
    const history = useHistory();

    switch (detailPage?.type) {
        case "risk":
            return (
                <div className="p-4">
                    <RiskDetails
                        {...detailPage?.data}
                        onMitigationTaskClicked={(taskId) => {
                            history.push(`?tab=task-management&taskId=${taskId}`);
                        }}
                        taskCreationSettings={{
                            enabled: true,
                            creationPayload: {
                                software: softwareData.id
                            }
                        }}
                        backButtonSettings={{
                            enabled: true,
                            onBtnClicked: () => {
                                history.push(`/admin/data-protection/softwares/${softwareData.id}?tab=risk-assessment`)
                                setDetailPage(null)
                            }
                        }}
                    />
                </div>
            );

        case "task":
            return (
                <div className="p-4">
                    <TaskDetails 
                        {...detailPage?.data} 
                        onBackClicked={() => {
                            history.push(`/admin/data-protection/softwares/${softwareData.id}?tab=task-management`)
                            setDetailPage(null)
                        }} 
                        onRelatedTaskClicked={(id) => {
                            setDetailPage({
                                type: "task",
                                data: {
                                    taskId: id
                                }
                            })
                        }}
                        ismsControlsOptions={{
                            software_id: softwareData.id
                        }}
                    />
                </div>
            );

        default:
            return null;
    }
};

const TabContent = ({
    isLoading,
    activeTab,
    detailPage,
    setDetailPage,
    softwareData
}) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    if (isLoading) {
        return null;
    }

    if (detailPage) {
        return (
            <DetailPage 
                softwareData={softwareData}
                setDetailPage={setDetailPage}
                detailPage={detailPage}
            />
        )
    } 

    switch (activeTab) {
        case "internal-comments":
            return (
                <InternalCommunication
                    assetId={softwareData.id}
                />
            )
        case "processing-activity":
            return (
                <ProcessingActivity />
            )
        case "monitoring": {
            const findingId = searchParams.get('id');

            if (findingId) {
                return (
                    <div className="p-4">
                        <FindingDetails
                            finding={{
                                id: findingId
                            }}
                            assetId={softwareData.id}
                        />
                    </div>
                )
            }
            else {
                return (
                    <Findings
                        assetId={softwareData.id}
                    />
                )
            }
        }
        case "related-incident":
            return (
                <RelatedIncident
                    assetId={softwareData.id}
                />
            )
        case "dpa":
            return (
                <DPA assetId={softwareData.id} />
            )
        case "risk-assessment":
            return (
                <RiskAssessment
                    assetId={softwareData.id}
                    onRiskSelected={(selectedRiskData) =>
                        setDetailPage({
                            type: "risk",
                            data: selectedRiskData,
                        })
                    }
                />
            )
        case "task-management":
            return (
                <TaskManager
                    assetId={softwareData.id}
                    onTaskSelected={(selectedTaskData) =>
                        setDetailPage({
                            type: "task",
                            data: selectedTaskData,
                        })
                    }
                />
            )
        case "dpia":
            return (
                <DPIA
                    assetId={softwareData.id}
                />
            )
        case "history-log":
            return (
                <HistoryLog assetId={softwareData.id} />
            )
        case "ai-risk-assessment":
            return (
                <AIRiskAssessment 
                    assetId={softwareData.id}
                />
            )
        case 'compliance': 
            return (
                <Compliance
                    assetId={softwareData.id}
                />
            )
        default:
            return <DataProtectionSoftwareInformation />
    }
}

const DataProtectionSoftwareDetails = ({
    t
}) => {
    const { softwareId } = useParams();
    const location = useLocation();
    const history = useHistory();
    const [activeTab, setActiveTab] = useState(null);
    const [showTabs, setShowTabs] = useState(true);
    const [detailPage, setDetailPage] = useState(null);
    const [navigatorOptions, setNavigatorOptions] = useState({
        enable: false,
        backButtonOptions: {},
        breadCrumbs: []
    });

    const redirectToSoftwaresList = useCallback(() => {
        history.push('/admin/data-protection/softwares?tab=all-software-assets');
    }, [
        history
    ]);

    const detailsPageNavigatorOptions = useMemo(() => {
        return {
            enable: true,
            backButtonOptions: {
                enable: true,
                onClick: redirectToSoftwaresList
            }
        }
    }, [
        redirectToSoftwaresList
    ]);

    const {
        data: softwareData,
        isFetching: softwareDataIsFetching
    } = useQuery({
        queryKey: ["gdpr-software-basic-information", softwareId],
        queryFn: async () => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.details(softwareId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);
            toast(t("An error occurred while fetching software details."), {
                type: "error",
            });
        },
    });

    const retryAiSetupMutation = useMutation({
        mutationFn: async () => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.retryAiSetup(softwareId);
        },
        onSuccess: () => {
            toast(t("AI setup retry has been started."), {
                type: "success",
            });
            redirectToSoftwaresList();
        },
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            const data = error?.response?.data;
            const errorKey =
                data?.error ||
                data?.message?.error ||
                (typeof data?.message === 'string' ? data.message : null);

            toast(t(errorKey || "An error occurred while retrying AI setup."), {
                type: "error",
            });
        },
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        let rootPageLink = `/admin/data-protection/softwares?tab=all-software-assets`

        let breads = [
            {
                title: 'Software Assets',
                link: rootPageLink
            },
            {
                title: `${softwareData?.service || '...'}`,
                link: `/admin/data-protection/softwares/${softwareId}?tab=basic-information`
            }
        ];

        let backButtonClick = null;

        const tabKey = searchParams.get('tab');

        switch (tabKey) {
            case 'basic-information':
                breads.push({
                    title: t('Basic Information'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                });
                break;
            case 'internal-comments':
                breads.push({
                    title: t('Internal Comments'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                });
                break;
            case 'processing-activity':
                breads.push({
                    title: t('Processing Activity'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                });
                break;
            case 'dpia':
                breads.push({
                    title: t('DPIA'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                });
                break;
            case 'monitoring':
                if (searchParams.has('id')) {
                    breads = [...breads, {
                        title: 'Monitoring',
                        link: `/admin/data-protection/softwares/${softwareId}?tab=monitoring`
                    }, {
                        title: 'Detail',
                        link: '#'
                    }];

                    backButtonClick = () => {
                        history.push(`/admin/data-protection/softwares/${softwareId}?tab=monitoring`);
                    }
                }
                else {
                    breads.push({
                        title: t('Monitoring'),
                        link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                    });
                }
                break;
            case 'related-incidents':
                breads.push({
                    title: t('Related Incidents'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                });
                break;
            case 'risk-assessment':
                if (searchParams.has('id')) {
                    breads = [...breads, {
                        title: 'Risk Assessment',
                        link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                    }, {
                        title: 'Detail',
                        link:  `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                    }];

                    backButtonClick = () => {
                        history.push(`/admin/data-protection/softwares/${softwareId}?tab=risk-assessment`);
                    }
                }
                else {
                    breads.push({
                        title: t('Risk Assessment'),
                        link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                    });
                }
                break;
            case 'task-management':
                if (searchParams.has('id')) {
                    breads = [...breads, {
                        title: 'Task Management',
                        link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                    }, {
                        title: 'Detail',
                        link:  `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                    }];

                    backButtonClick = () => {
                        history.push(`/admin/data-protection/softwares/${softwareId}?tab=task-management`);
                    }   
                }
                else {
                    breads.push({
                        title: t('Task Management'),
                        link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                    });
                }
                break;
            case 'dpa':
                breads.push({
                    title: t('Data Protection Authority'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                });
                break;
            case 'compliance':
                breads.push({
                    title: t('Compliance'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                });
                break;
            case 'history-log':
                breads.push({
                    title: t('History Log'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=${tabKey}`
                });
                break;
            default:
                breads.push({
                    title: t('Basic Information'),
                    link: `/admin/data-protection/softwares/${softwareId}?tab=basic-information`
                });
                break;
        }

        const softwareName = softwareData?.service ? `${softwareData.service}-ID: ${softwareData.secondary_id}` : '...';

        setNavigatorOptions({
            ...detailsPageNavigatorOptions,
            backButtonOptions: {
                ...detailsPageNavigatorOptions.backButtonOptions,
                onClick : backButtonClick || redirectToSoftwaresList
            },
            breadCrumbs: breads,
            pageTitle: softwareName
        });
    }, [
        location.search, 
        softwareData,
        detailsPageNavigatorOptions,
        redirectToSoftwaresList,
        history,
        softwareId,
        t
    ]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        let isSpecialTab = [
            'monitoring',
            'risk-assessment',
            'task-management'
        ].includes(activeTab?.key) && searchParams.has('id');

        if (isSpecialTab && activeTab?.key === 'risk-assessment') {
            if (!detailPage) {
                setDetailPage({
                    type: 'risk',
                    data: {
                        riskId: searchParams.get('id')
                    }
                })
            }
        }

        if (isSpecialTab && activeTab?.key === 'task-management') {
            if (!detailPage) {
                setDetailPage({
                    type: 'task',
                    data: {
                        taskId: searchParams.get('id')
                    }
                })
            }
        }

        if (isSpecialTab) {
            setShowTabs(false)
        }
        else {
            setShowTabs(true)
            setDetailPage(null)
        }
    }, [ location.search, activeTab ]);

    return (
        <div className='page-content data-protection-software-details-page software-details-page'>
            <Container fluid>
                <Row>
                    {
                        navigatorOptions.enable && (
                            <Col lg="12" className="mb-4">
                                <Navigator backButtonOptions={(props) => {
                                    return {
                                        ...props,
                                        ...navigatorOptions.backButtonOptions
                                    }
                                }} breadCrumbs={navigatorOptions.breadCrumbs} pageTitle={navigatorOptions.pageTitle} />
                            </Col>
                        )
                    }

                    <Col lg="12" className="mb-4">
                        {softwareData?.ai_process_status === 'failed' && (
                            <Alert color="danger" className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <strong className="d-block mb-1">
                                        {t("AI setup failed")}
                                    </strong>
                                    <span>
                                        {softwareData?.ai_process_error
                                            ? t(softwareData.ai_process_error)
                                            : t("The AI setup could not be completed. Please review the notification for more details.")}
                                    </span>
                                </div>

                                <Button
                                    color="danger"
                                    size="sm"
                                    className="ms-3 flex-shrink-0"
                                    disabled={retryAiSetupMutation.isLoading}
                                    onClick={() => retryAiSetupMutation.mutate()}
                                >
                                    {retryAiSetupMutation.isLoading ? (
                                        <Spinner size="sm" animation="border" />
                                    ) : (
                                        t("Retry AI setup")
                                    )}
                                </Button>
                            </Alert>
                        )}

                        <div className="details-content-wrapper">
                            {showTabs && (
                                <Tabs
                                    isLoading={softwareDataIsFetching}
                                    onActiveTabChange={({ tab }) => setActiveTab(tab)}
                                    softwareData={softwareData}
                                />
                            )}

                            <TabContent
                                isLoading={softwareDataIsFetching}
                                activeTab={activeTab?.key}
                                softwareData={softwareData}
                                detailPage={detailPage}
                                setDetailPage={setDetailPage}
                            />
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    )
};

export default withNamespaces()(
    withRouter(memo(DataProtectionSoftwareDetails))
);
